import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Analytics,
  Assessment,
  Business,
  Chat,
  Explore,
  Headphones,
  KeyboardArrowDown,
  RateReview,
  Science,
  Search,
  Sync,
  Transform,
} from '@mui/icons-material';
import { Button, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';

export function AppHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    handleMenuClose();
  };

  const getCurrentSection = () => {
    if (location.pathname.includes('/template-model-conversion')) return 'Template Model Conversion';
    if (location.pathname.includes('/template-conversion-output-viewer')) return 'Conversion Output';
    if (location.pathname.includes('/feature-explorer')) return 'Feature Explorer';
    if (location.pathname.includes('/encounter-telemetry-sessions')) return 'Encounter Telemetry';
    if (location.pathname.includes('/shadow-notes')) return 'Shadow Notes';
    if (location.pathname.includes('/streaming-thread/')) return 'Thread Details';
    if (location.pathname.includes('/streaming-threads')) return 'Streaming Threads';
    if (location.pathname.includes('/payers/sync')) return 'Payer Sync';
    if (location.pathname.includes('/feedback/sessions')) return 'Feedback Sessions';
    if (location.pathname.includes('/feedback')) return 'Feedback Dashboard';
    if (location.pathname.includes('/jotaudit')) return 'JotAudit';
    if (location.pathname.includes('/capability/')) return 'Capability Details';
    if (location.pathname.includes('/capabilities')) return 'Capabilities';
    if (location.pathname.includes('/bundle/')) return 'Bundle Details';
    if (location.pathname.includes('/bundles')) return 'Bundles';
    if (location.pathname.includes('/organization/')) return 'Organization Details';
    if (location.pathname.includes('/organizations')) return 'Organizations';
    if (location.pathname.includes('/provider/')) return 'User Details';
    if (location.pathname.includes('/note/')) return 'Note Details';
    if (location.pathname.includes('/encounter/')) return 'Encounter Details';
    if (location.pathname.includes('/transcript/')) return 'Transcript Details';
    if (location.pathname.includes('/appointment/')) return 'Appointment Details';
    if (location.pathname === '/admin-v2') return 'Search';
    return 'Admin';
  };

  return (
    <header className="bg-white shadow-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center gap-4">
            <div className="flex-shrink-0">
              <Headphones className="h-8 w-8 text-blue-600" />
            </div>
            <div className="ml-4">
              <h1 className="text-xl font-semibold text-gray-900">Admin v2</h1>
            </div>

            {/* Navigation Dropdown */}
            <Button
              variant="outlined"
              size="small"
              endIcon={<KeyboardArrowDown />}
              onClick={handleMenuClick}
              sx={{ ml: 2 }}
            >
              {getCurrentSection()}
            </Button>
            <Menu
              anchorEl={anchorEl}
              open={menuOpen}
              onClose={handleMenuClose}
              MenuListProps={{
                'aria-labelledby': 'navigation-button',
              }}
            >
              <MenuItem onClick={() => handleNavigate('/admin-v2')}>
                <ListItemIcon>
                  <Search fontSize="small" />
                </ListItemIcon>
                <ListItemText>Search</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => handleNavigate('/admin-v2/organizations')}>
                <ListItemIcon>
                  <Business fontSize="small" />
                </ListItemIcon>
                <ListItemText>Organizations</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => handleNavigate('/admin-v2/jotaudit')}>
                <ListItemIcon>
                  <Assessment fontSize="small" />
                </ListItemIcon>
                <ListItemText>JotAudit Dashboard</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => handleNavigate('/admin-v2/feedback')}>
                <ListItemIcon>
                  <RateReview fontSize="small" />
                </ListItemIcon>
                <ListItemText>Feedback Dashboard</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => handleNavigate('/admin-v2/encounter-telemetry-sessions')}>
                <ListItemIcon>
                  <Analytics fontSize="small" />
                </ListItemIcon>
                <ListItemText>Encounter Telemetry Sessions</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => handleNavigate('/admin-v2/feature-explorer')}>
                <ListItemIcon>
                  <Explore fontSize="small" />
                </ListItemIcon>
                <ListItemText>Feature Explorer</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => handleNavigate('/admin-v2/shadow-notes')}>
                <ListItemIcon>
                  <Science fontSize="small" />
                </ListItemIcon>
                <ListItemText>Shadow Notes</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => handleNavigate('/admin-v2/streaming-threads')}>
                <ListItemIcon>
                  <Chat fontSize="small" />
                </ListItemIcon>
                <ListItemText>Streaming Threads</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => handleNavigate('/admin-v2/template-model-conversion')}>
                <ListItemIcon>
                  <Transform fontSize="small" />
                </ListItemIcon>
                <ListItemText>Template Model Conversion</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => handleNavigate('/admin-v2/payers/sync')}>
                <ListItemIcon>
                  <Sync fontSize="small" />
                </ListItemIcon>
                <ListItemText>Payer Sync</ListItemText>
              </MenuItem>
            </Menu>
          </div>
          <div className="flex items-center">
            <a
              href="/admin"
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Go to Admin v1
            </a>
          </div>
        </div>
      </div>
    </header>
  );
}
